import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { useLocation } from "react-router-dom";
import { STATUS } from "../../common/Const";
import { getConfiguredLessonTypeOptions } from "../../common/lessonTypes";
import { hasErrors, hasNoErrors } from "../../common/Helpers";
import { history } from "../../../redux/store/configureStore";
import * as productActions from "../../../redux/actions/productActions";
import SettingsService from "../../../services/SettingsService";
import { validateProduct } from "../../billingEnrollment/billingEnrollmentValidation";
import { buildManageRouteWithSchoolYear, getNormalizedSchoolYearStartYearFromSearch } from "../manageSchoolYearRouting";
import ProductForm from "./ProductForm";

export function Product({ product: initialProduct, products, loadProducts, saveProduct }) {
  const location = useLocation();
  const activeSchoolYearStartYear = getNormalizedSchoolYearStartYearFromSearch(location.search);
  const [product, setProduct] = useState({ ...initialProduct });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState(STATUS.IDLE);
  const [classTypes, setClassTypes] = useState(getConfiguredLessonTypeOptions([], initialProduct.classTypeId));

  useEffect(() => {
    if (products.length === 0) {
      loadProducts().catch(error => alert(`Loading products failed ${error}`));
    } else {
      setProduct({ ...initialProduct });
    }
  }, [initialProduct, loadProducts, products.length]);

  useEffect(() => {
    Promise.resolve()
      .then(() => SettingsService.getSettings())
      .then(settings => setClassTypes(getConfiguredLessonTypeOptions(settings, product.classTypeId)))
      .catch(() => setClassTypes(getConfiguredLessonTypeOptions([], product.classTypeId)));
  }, [product.classTypeId]);

  const validate = (nextProduct = product) => {
    const nextErrors = validateProduct(nextProduct);
    setErrors(nextErrors);
    return hasNoErrors(nextErrors);
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const numericFields = ["defaultPrice", "validForDays", "packageUnits"];
    const nextProduct = {
      ...product,
      [name]: numericFields.includes(name) && value !== "" ? Number(value) : value,
    };
    setProduct(nextProduct);
    validate(nextProduct);
  };

  const handleCancel = () => {
    history.push(buildManageRouteWithSchoolYear("/manage/products", "", activeSchoolYearStartYear));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setStatus(STATUS.SUBMITTING);
    if (!validate()) {
      setStatus(STATUS.SUBMITTED);
      return;
    }

    saveProduct(product)
      .then(() => {
        setStatus(STATUS.COMPLETED);
        handleCancel();
      })
      .catch((error) => {
        setErrors({ ...errors, saveError: error.message });
        setStatus(STATUS.SUBMITTED);
      });
  };

  return (
    <div className="mb-5">
      {hasErrors(errors) && status === STATUS.SUBMITTED && (
        <div role="alert" className="alert alert-danger">
          {Object.keys(errors).map(key => <div key={key}>{errors[key]}</div>)}
        </div>
      )}
      <ProductForm
        product={product}
        classTypes={classTypes}
        errors={errors}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        status={status}
      />
    </div>
  );
}

Product.propTypes = {
  product: PropTypes.object.isRequired,
  products: PropTypes.array.isRequired,
  loadProducts: PropTypes.func.isRequired,
  saveProduct: PropTypes.func.isRequired,
};

const newProduct = {};
const getProductById = (products, id) => products.find(product => product.id === id) || {};

const mapStateToProps = (state, ownProps) => {
  const { id } = ownProps.match.params;
  const product = id === "new" || state.products.length === 0 ? newProduct : getProductById(state.products, id);

  return {
    product,
    products: state.products,
  };
};

const mapDispatchToProps = dispatch => ({
  loadProducts: () => dispatch(productActions.loadProducts()),
  saveProduct: product => dispatch(productActions.saveProduct(product)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Product);
